import { useMemo, useEffect, useState, useCallback } from 'react';

import ScheduledLesson from '@/models/ScheduledLesson';
import { ScheduledLessonWithoutLessonErrorDispatcher } from '@/errors/scheduledLesson';
import { ErrorCapturer } from '@/adapters/ErrorCapturer';

interface UseValidatedScheduledLessonsOptions {
  scheduledLessons: ScheduledLesson[];
  klassId?: number;
}

interface UseValidatedScheduledLessonsReturn {
  invalidScheduledLessons: ScheduledLesson[];
  pastScheduledLessons: ScheduledLesson[];
  currentScheduledLessons: ScheduledLesson[];
  hasInvalidLessons: boolean;
}

export function useValidatedScheduledLessons({
  scheduledLessons,
  klassId,
}: UseValidatedScheduledLessonsOptions): UseValidatedScheduledLessonsReturn {
  const [hasInvalidLessons, setHasInvalidLessons] = useState(false);

  const { invalidLessons } = useMemo(() => {
    const invalidLessons = scheduledLessons.filter(({ lesson }) => !lesson?.id);

    return { invalidLessons };
  }, [scheduledLessons]);

  const lastFinishedScheduledLesson =
    getLastFinishedScheduledLesson(scheduledLessons);
  const pastScheduledLessons = useMemo(
    () =>
      getPastScheduledLessons(scheduledLessons, lastFinishedScheduledLesson),
    [scheduledLessons, lastFinishedScheduledLesson],
  );
  const currentScheduledLessons = useMemo(
    () =>
      getCurrentScheduledLessons(scheduledLessons, lastFinishedScheduledLesson),
    [scheduledLessons, lastFinishedScheduledLesson],
  );

  const dispatchError = useCallback(
    (invalidScheduledLessons: ScheduledLesson[]) => {
      const errorDispatcher = new ScheduledLessonWithoutLessonErrorDispatcher(
        invalidScheduledLessons.map(({ id }) => id),
        Number(klassId),
        window.location.pathname,
      );

      const errorCapturer = new ErrorCapturer(errorDispatcher);
      errorCapturer.dispatchError();
    },
    [klassId],
  );

  useEffect(() => {
    if (!invalidLessons.length) {
      setHasInvalidLessons(false);

      return;
    }

    setHasInvalidLessons(true);
    dispatchError(invalidLessons);
  }, [invalidLessons, dispatchError]);

  return {
    invalidScheduledLessons: invalidLessons,
    pastScheduledLessons,
    currentScheduledLessons,
    hasInvalidLessons,
  };
}

function getLastFinishedScheduledLesson(
  scheduledLessons: ScheduledLesson[],
): ScheduledLesson | undefined {
  return scheduledLessons
    .filter(({ hasDone }) => hasDone)
    .sort((a, b) => b.order - a.order)
    .at(0);
}

function getPastScheduledLessons(
  scheduledLessons: ScheduledLesson[],
  lastFinishedScheduledLesson: ScheduledLesson | undefined,
): ScheduledLesson[] {
  if (!lastFinishedScheduledLesson) return [];

  return scheduledLessons.filter(
    ({ order }) => order <= lastFinishedScheduledLesson.order,
  );
}

function getCurrentScheduledLessons(
  scheduledLessons: ScheduledLesson[],
  lastFinishedScheduledLesson: ScheduledLesson | undefined,
): ScheduledLesson[] {
  if (!lastFinishedScheduledLesson) return scheduledLessons;

  return scheduledLessons.filter(
    ({ order }) => order > lastFinishedScheduledLesson.order,
  );
}
